<template>
  <div class="flex flex-col divide-y divide-utility-gray-200">
    <Setting
      v-for="(item, index) in configurableSettings"
      class="pb-6 px-6 md:px-8"
      :key="index"
      :title="item.title"
      :description="item.description"
      :class="first(configurableSettings) === item ? 'pt-6' : 'pt-5'"
    >
      <template v-if="item.dropdown" #body>
        <div class="w-full max-w-[500px]">
          <Select
            v-model="settings[item.dropdown.setting]"
            :options="item.dropdown.items"
            :disabled="item.dropdown.disabled"
          />
        </div>
      </template>
      <template #switch>
        <Switch
          size="sm"
          :checked="settings[item.setting]"
          :disabled="item.disabled"
          @update:checked="settings[item.setting] = !settings[item.setting]"
        />
      </template>
    </Setting>
    <Setting
      class="py-5 pb-12 px-6 md:px-8"
      :title="t('admin.settings.messenger.languages.title')"
      :description="t('admin.settings.messenger.languages.description')"
    >
      <template #body>
        <div
          class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-4"
        >
          <div
            v-for="language in languages"
            class="flex gap-2 items-center"
            :key="language.locale"
          >
            <Checkbox
              class="w-4 h-4 p-0.5 rounded shadow-none"
              :checked="settings.allowed_messenger_locales[language.locale]"
              :disabled="!allowedToUpdate"
              @update:checked="updateLanguages(language.locale)"
            />
            <div class="text-utility-gray-700 text-sm font-medium">
              {{ language.label }}
            </div>
          </div>
        </div>
      </template>
    </Setting>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { inject, watch, ref, computed } from "vue";
import { router } from "@inertiajs/vue3";
import { Setting } from "@/Pages/Settings/MessengerFlow/Partials";
import { Switch } from "@/SharedComponents/Switchers";
import route from "ziggy-js";
import { Select } from "@/Components/Select";
import { Checkbox } from "@/Shadcn/Components/ui/checkbox";
import { first } from "lodash";

const { t, messages } = useI18n();
const { hasPermission, permissions } = inject("permissions");

const props = defineProps({
  tenant: { type: Object, required: true },
  notificationOptions: { type: Array, required: true },
});

const settings = ref(props.tenant.settings);

const allowedToUpdate = hasPermission(permissions.updateMessengerFlow);

const configurableSettings = computed(() => [
  {
    title: t("admin.settings.messenger.require_contact_details_report.title"),
    description: t(
      "admin.settings.messenger.require_contact_details_report.description"
    ),
    setting: "require_contact_details",
    disabled: !allowedToUpdate,
    dropdown: {
      setting: "notification_options",
      items: settings.value.require_contact_details
        ? props.notificationOptions.filter((item) => item.value !== "none")
        : props.notificationOptions,
      disabled: !allowedToUpdate,
    },
  },
  {
    title: t("admin.settings.messenger.require_name.title"),
    description: t("admin.settings.messenger.require_name.description"),
    setting: "require_name",
    disabled: !allowedToUpdate,
    dropdown: false,
  },
  {
    title: t("admin.settings.messenger.disable_space_selection.title"),
    description: t(
      "admin.settings.messenger.disable_space_selection.description"
    ),
    setting: "disable_space_selection",
    disabled: !allowedToUpdate,
    dropdown: false,
  },
  {
    title: t("admin.settings.messenger.hide_reported_problems.title"),
    description: t(
      "admin.settings.messenger.hide_reported_problems.description"
    ),
    setting: "hide_reported_problems",
    disabled: !allowedToUpdate,
    dropdown: false,
  },
  {
    title: t("admin.settings.messenger.disable_notifications.title"),
    description: t(
      "admin.settings.messenger.disable_notifications.description"
    ),
    setting: "disable_notifications",
    disabled: !allowedToUpdate,
    dropdown: false,
  },
]);

const languages = computed(() => {
  return Object.keys(messages.value).map((lang) => ({
    label: t(`validation.values.locale.${lang}`),
    locale: lang,
  }));
});

watch(settings.value, () => {
  updateSettings();
});

const updateSettings = () => {
  if (
    settings.value.require_contact_details &&
    settings.value.notification_options === "none"
  ) {
    settings.value.notification_options = props.notificationOptions[0].value;
  }

  router.patch(route("settings.messenger.update", [props.tenant]), {
    settings: settings.value,
  });
};

const updateLanguages = (locale) => {
  if (settings.value.allowed_messenger_locales.length === 0) {
    settings.value.allowed_messenger_locales = {};
  }

  if (settings.value.allowed_messenger_locales[locale]) {
    delete settings.value.allowed_messenger_locales[locale];
  } else {
    settings.value.allowed_messenger_locales[locale] = true;
  }
};
</script>
